import { useQuery } from '@apollo/client';
import { Card, CardBody, Link } from '@apollo/orbit';
import React, { useEffect } from 'react';
import { Redirect, Switch } from 'react-router-dom';
import { Navigate, useLocation } from 'react-router-dom-v5-compat';

import { GraphOnboardingRoutes } from 'src/app/account/graphOnboarding/GraphOnboardingRoutes';
import { graphOnboardingRoutes } from 'src/app/account/graphOnboarding/routes';
import { raw as ImageRocket } from 'src/assets/rocket.svg';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { SuperAdminWarningBanner } from 'src/components/common/superAdminWarningBanner/SuperAdminWarningBanner';
import { ContactSupportButton } from 'src/components/ContactSupportCTAs';
import { DocumentTitle } from 'src/components/documentTitle/DocumentTitle';
import { PermissionBlockCard } from 'src/components/permissionGuards/permissionBlockCard/PermissionBlockCard';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { usePermissions } from 'src/components/permissionGuards/usePermissions';
import { PlanProtectedArea } from 'src/components/planProtectedArea/PlanProtectedArea';
import { PricingLink } from 'src/components/pricingLink/PricingLink';
import { SidebarControlsProvider } from 'src/components/sidebar/useSidebarControls';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useCurrentSubscription } from 'src/hooks/useCurrentSubscription';
import { useQueryParam } from 'src/hooks/useQueryParam';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { SentryRoute as Route } from 'src/hooks/useTelemetryInitializer';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { useShowApiKeysTab } from 'src/lib/featureFlags';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';

import { ApiKeyManagementPage } from '../apiKeys/ApiKeyManagementPage';
import { ChampionDashboardPage } from '../championDashboardPage/ChampionDashboard';
import * as orgRoutes from '../routes';
import { FlowTypeProvider } from '../supergraphListPage/newSupergraphOnboarding/FlowTypeProvider';
import { NewSupergraphOnboarding } from '../supergraphListPage/newSupergraphOnboarding/NewSupergraphOnboarding';
import { SupergraphsListPage } from '../supergraphListPage/SupergraphListPage';
import { TabbedAccountLayout } from '../tabbedAccountLayout/TabbedAccountLayout';

import { ACCOUNT_LAYOUT_QUERY } from './AccountLayoutQuery';

// `React.lazy` assumes we're always using default exports and will internally
// try to access `.default` property of the result. We have to use this
// workaround to use named exports.
// @see https://github.com/reactjs/rfcs/pull/64#issuecomment-431507924
const OrgMembersPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "OrgMembersPage" */ '../orgMembersPage/OrgMembersPage'
    )
  ).OrgMembersPage,
}));
const OrgSettingsPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "OrgSettingsLayout" */ '../orgSettingsPage/components/OrgSettingsLayout'
    )
  ).OrgSettingsLayout,
}));
const OrgUsagePage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "OrgUsagePage" */ '../orgUsagePage/OrgUsagePage'
    )
  ).OrgUsagePage,
}));

export const AccountLayout = () => {
  const location = useLocation();
  const { orgId } = useRouteParams(orgRoutes.org);
  const overlayName = useQueryParam('overlay');
  const [, setCurrentAccountId] = useCurrentAccountId();

  const showApiKeysTab = useShowApiKeysTab();

  useEffect(() => {
    setCurrentAccountId(orgId);
  }, [setCurrentAccountId, orgId]);

  const { data, loading, error } = useQuery<
    GraphQLTypes.Account__AccountLayoutQuery,
    GraphQLTypes.Account__AccountLayoutQueryVariables
  >(ACCOUNT_LAYOUT_QUERY, {
    variables: {
      accountId: orgId,
    },
    errorPolicy: 'all',
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  const { data: permissionsData } = usePermissions(orgId, undefined, undefined);
  const canQueryBilling =
    !!permissionsData?.accountPermissions?.canQueryBillingInfo;

  const currentPlanResult = useCurrentPlan({
    accountId: orgId,
  });

  const { currentSubscription } = useCurrentSubscription({
    accountId: orgId,
    canQueryBilling,
  });

  if (
    loading ||
    currentPlanResult.loading ||
    // useQuery is currently returning data for the previous accountId and not returning loading true
    // This checks to make sure the returned account is in fact the same as the current one in the url
    (data?.account?.secondaryIDs &&
      ![data.account.id, ...data.account.secondaryIDs].includes(orgId))
  ) {
    return <Loading />;
  }

  if (
    !data?.account &&
    data?.me?.__typename === 'User' &&
    data.me.type === GraphQLTypes.UserType.SSO
  ) {
    // Special-case: SSO users only have one organization, so we can just
    // redirect them back to the top level page, which will redirect
    // them to their org.
    return (
      <Redirect
        to={{
          ...location,
          pathname: '/',
        }}
      />
    );
  }

  const currentPlan = currentPlanResult?.currentPlan;
  const account = data?.account;
  if (
    error ||
    currentPlanResult.error ||
    !account ||
    !data?.me ||
    !currentPlan
  ) {
    return (
      <NotFound
        title="Houston, we have a problem!"
        subtitle={
          <>
            The organization you are looking for could not be found. Please
            select a different <b>Organization</b> from the left navigation.
          </>
        }
      />
    );
  }

  // If the org has been renamed (has had its ID changed) and the ID
  // in the URL is a secondary (old) ID for the org, then the server
  // will return a different ID for our query than the one we used. In
  // that case, redirect us over to the primary (current) ID, preserving
  // the rest of the URL.
  if (account.id !== orgId) {
    const newPathname = location.pathname.replace(
      orgRoutes.org.location({ orgId }).pathname,
      orgRoutes.org.location({ orgId: account.id }).pathname,
    );
    return (
      <Redirect
        to={{
          ...location,
          pathname: newPathname,
        }}
      />
    );
  }

  return (
    <SidebarControlsProvider>
      <div className="relative flex flex-1 flex-col overflow-y-auto">
        {data.me.__typename === 'User' &&
          !data.me.memberships.some(
            (m) => m.account.id === data?.account?.id,
          ) &&
          data.me.internalAdminRole && (
            <SuperAdminWarningBanner
              internalAdminRole={data.me.internalAdminRole}
              accountId={account.id}
            />
          )}
        <DocumentTitle title={account.name} />
        <Switch>
          <Route
            exact
            path={orgRoutes.org.definition}
            render={() => (
              <Navigate
                replace={true}
                to={orgRoutes.graphs.locationFrom({
                  fromRouteConfig: orgRoutes.org,
                  location,
                  patch: {
                    // TODO: ideally org would have 'upgrade' which is the overlay
                    // we need to be forwarded here. But routeconfigs doesn't handle the same param with different types well.
                    overlay: orgRoutes.isGraphsOverlay(overlayName)
                      ? overlayName
                      : undefined,
                  },
                })}
              />
            )}
          />
          <Route
            exact
            path={orgRoutes.legacyBilling.definition}
            render={() => (
              <Navigate
                replace={true}
                to={orgRoutes.settings.path({ orgId: account.id })}
              />
            )}
          />
          <Route
            exact
            path={orgRoutes.legacyServices.definition}
            render={() => (
              <Navigate
                replace={true}
                to={orgRoutes.graphs.path({ orgId: account.id })}
              />
            )}
          />
          <Route
            exact
            path={orgRoutes.legacySupergraphs.definition}
            render={() => (
              <Navigate
                replace={true}
                to={orgRoutes.graphs.path({ orgId: account.id })}
              />
            )}
          />
          <Route path={orgRoutes.dashboard.definition} exact>
            <PlanProtectedArea accountId={account.id}>
              <DocumentTitle title={[account.name, 'Dashboard']} />
              <TabbedAccountLayout
                accountId={account.id}
                accountName={account.name}
                planName={currentPlanResult.currentPlan?.name}
                planKind={currentPlanResult.currentPlan?.kind}
                trialEndDate={currentSubscription?.trialExpiresAt}
              >
                <PermissionGuard
                  accountId={account.id}
                  accountPermissions={['canQueryStats', 'canReadTickets']}
                  fallback={PermissionBlockCard}
                >
                  <React.Suspense fallback={<Loading />}>
                    <ChampionDashboardPage
                      accountId={account.id}
                      maxRangeInDays={
                        currentPlanResult.currentPlan?.maxRangeInDays ?? 1
                      }
                    />
                  </React.Suspense>
                </PermissionGuard>
              </TabbedAccountLayout>
            </PlanProtectedArea>
          </Route>
          <Route path={orgRoutes.members.definition} exact>
            <PlanProtectedArea accountId={account.id}>
              <DocumentTitle title={[account.name, 'Org Members']} />
              <TabbedAccountLayout
                accountId={account.id}
                accountName={account.name}
                planName={currentPlanResult.currentPlan?.name}
                planKind={currentPlanResult.currentPlan?.kind}
                trialEndDate={currentSubscription?.trialExpiresAt}
              >
                <React.Suspense fallback={<Loading />}>
                  <OrgMembersPage />
                </React.Suspense>
              </TabbedAccountLayout>
            </PlanProtectedArea>
          </Route>
          <Route path={orgRoutes.settings.definition} exact>
            <PlanProtectedArea accountId={account.id}>
              <DocumentTitle title={[account.name, 'Org Settings']} />
              <PermissionGuard
                accountId={account.id}
                accountPermissions={[
                  'canRemoveMembers',
                  'canSetConstrainedPlan',
                  'canQueryBillingInfo',
                  'canUpdateMetadata',
                ]}
                fallback={PermissionBlockCard}
              >
                <TabbedAccountLayout
                  accountId={account.id}
                  accountName={account.name}
                  planName={currentPlanResult.currentPlan?.name}
                  planKind={currentPlanResult.currentPlan?.kind}
                  trialEndDate={currentSubscription?.trialExpiresAt}
                >
                  <React.Suspense fallback={<Loading />}>
                    <OrgSettingsPage />
                  </React.Suspense>
                </TabbedAccountLayout>
              </PermissionGuard>
            </PlanProtectedArea>
          </Route>
          {showApiKeysTab && (
            <Route path={orgRoutes.apiKeys.definition} exact>
              <PlanProtectedArea accountId={account.id}>
                <DocumentTitle title={[account.name, 'API Keys']} />
                <TabbedAccountLayout
                  accountId={account.id}
                  accountName={account.name}
                  planName={currentPlanResult.currentPlan?.name}
                  planKind={currentPlanResult.currentPlan?.kind}
                  trialEndDate={currentSubscription?.trialExpiresAt}
                >
                  <React.Suspense fallback={<Loading />}>
                    <ApiKeyManagementPage />
                  </React.Suspense>
                </TabbedAccountLayout>
              </PlanProtectedArea>
            </Route>
          )}
          <Route path={orgRoutes.usage.definition} exact>
            <PlanProtectedArea accountId={account.id}>
              <DocumentTitle title={[account.name, 'Audit Logs']} />
              <TabbedAccountLayout
                accountId={account.id}
                accountName={account.name}
                planName={currentPlanResult.currentPlan?.name}
                planKind={currentPlanResult.currentPlan?.kind}
                trialEndDate={currentSubscription?.trialExpiresAt}
              >
                {currentPlan.maxAuditInDays > 0 ? (
                  <React.Suspense fallback={<Loading />}>
                    <OrgUsagePage accountId={account.id} />
                  </React.Suspense>
                ) : (
                  <div className="flex min-h-full flex-col">
                    <Card className="m-8 flex-1">
                      <CardBody className="size-full items-center justify-center">
                        <div className="max-w-[37rem] text-center">
                          <img
                            className="mb-5"
                            src={ImageRocket}
                            alt="rocket"
                          />
                          <div className="mb-4 text-3xl font-semibold">
                            Upgrade to Enterprise to use Audit Logs
                          </div>
                          <div className="text-xl leading-7">
                            Use Audit Logs to easily generate detailed action
                            records of user activity to monitor changes and
                            maintain security. Learn more about{' '}
                            <Link isExternal to={DocsRouter.path('AuditLogs')}>
                              audit logs
                            </Link>{' '}
                            by visiting our docs. See additional Enterprise
                            features <PricingLink>here</PricingLink>.
                            <br />
                            <ContactSupportButton className="mt-6">
                              Contact us
                            </ContactSupportButton>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                )}
              </TabbedAccountLayout>
            </PlanProtectedArea>
          </Route>
          <Route path={orgRoutes.graphs.definition} exact>
            <PlanProtectedArea accountId={account.id}>
              <DocumentTitle title={[account.name, 'Graph List']} />
              <TabbedAccountLayout
                accountId={account.id}
                accountName={account.name}
                planName={currentPlanResult.currentPlan?.name}
                planKind={currentPlanResult.currentPlan?.kind}
                trialEndDate={currentSubscription?.trialExpiresAt}
              >
                <React.Suspense fallback={<Loading />}>
                  <SupergraphsListPage />
                </React.Suspense>
              </TabbedAccountLayout>
            </PlanProtectedArea>
          </Route>
          <Route
            path={[...graphOnboardingRoutes]}
            component={GraphOnboardingRoutes}
          />
          <Route path={orgRoutes.supergraphOnboarding.definition} exact>
            <PlanProtectedArea accountId={account.id}>
              <DocumentTitle title={[account.name ?? 'New Supergraph']} />
              <TabbedAccountLayout
                accountId={account.id}
                accountName={account.name}
                planName={currentPlanResult.currentPlan?.name}
                planKind={currentPlanResult.currentPlan?.kind}
                trialEndDate={currentSubscription?.trialExpiresAt}
              >
                <React.Suspense fallback={<Loading />}>
                  <FlowTypeProvider>
                    <NewSupergraphOnboarding />
                  </FlowTypeProvider>
                </React.Suspense>
              </TabbedAccountLayout>
            </PlanProtectedArea>
          </Route>
          <Route component={NotFound} />
        </Switch>
      </div>
    </SidebarControlsProvider>
  );
};
